/*
 * Copyright (c) All right reserved Anvish Inc. 2025
 * This project and any portion thereof may not be reproduced or used in any manner without the express written permission of Anvish Inc.
 */
"use client";
import React, { useEffect, useState } from "react";
import { Button } from "@heroui/button";
import { useRouter } from "next/navigation";
import "ldrs/react/Bouncy.css";

import { Spinner } from "@heroui/spinner";

import FilterSelect from "@/components/element/CategoryTabbedFilter/FilterSelect";
import { FetchDataServerAction } from "@/services/FetchDataServerAction";

interface MakeModelFilterProps {
  redirectUrl: string;
  csrfToken: string;
  isSingleColumn?: boolean;
}

const MakeModelFilter: React.FC<MakeModelFilterProps> = ({
  redirectUrl,
  csrfToken,
  isSingleColumn = false,
}) => {
  const router = useRouter();
  const [make, setMake] = useState<string[]>([]);
  const [model, setModel] = useState<string[]>([]);
  const [engine, setEngine] = useState<string[]>([]);
  const [year, setYear] = useState<string[]>([]);
  const [frame, setFrame] = useState<string[]>([]);
  const [selectedFilters, setSelectedFilters] = useState({
    make: "",
    model: "",
    engine: "",
    year: "",
    frame: "",
  });
  const [catalogueId, setCatalogueId] = useState(null);
  const [loading, setLoading] = useState(true);

  const getFilterData = async ({
    trigger = null,
    filters = {},
  }: {
    trigger: string | null;
    filters: any;
  }) => {
    setLoading(true);
    setSelectedFilters(filters);
    const resp = await FetchDataServerAction(
      csrfToken,
      `ecomm/make-model-filter/`,
      {
        filter: filters,
      },
    );

    if (!trigger) {
      setMake(resp);
    }
    if (trigger === "make") {
      setModel(resp);
    }
    if (trigger === "model") {
      setEngine(resp);
    }
    if (trigger === "engine") {
      setYear(resp);
    }
    if (trigger === "year") {
      setFrame(resp);
    }
    if (trigger === "frame") {
      setCatalogueId(resp[0].id);
    }
    setLoading(false);
  };

  useEffect(() => {
    if (make.length === 0) {
      getFilterData({ trigger: null, filters: {} });
    }
  }, []);

  const handleFilterChange = (trigger: string, value: string) => {
    let filterData: any = {};

    if (trigger === "make") {
      setModel([]);
      setEngine([]);
      setYear([]);
      setFrame([]);
      setCatalogueId(null);
      filterData = {
        make: value,
        model: "",
        engine: "",
        year: "",
        frame: "",
      };

      getFilterData({ trigger: "make", filters: filterData });
    }
    if (trigger === "model") {
      setEngine([]);
      setYear([]);
      setFrame([]);
      setCatalogueId(null);

      filterData = {
        ...selectedFilters,
        model: value,
        engine: "",
        year: "",
        frame: "",
      };
      getFilterData({ trigger: "model", filters: filterData });
    }
    if (trigger === "engine") {
      setYear([]);
      setFrame([]);
      setCatalogueId(null);

      filterData = {
        ...selectedFilters,
        engine: value,
        year: "",
        frame: "",
      };
      getFilterData({ trigger: "engine", filters: filterData });
    }
    if (trigger === "year") {
      setFrame([]);
      setCatalogueId(null);

      filterData = {
        ...selectedFilters,
        year: value,
        frame: "",
      };
      getFilterData({ trigger: "year", filters: filterData });
    }
    if (trigger === "frame") {
      setCatalogueId(null);
      filterData = {
        ...selectedFilters,
        frame: value,
      };
      getFilterData({ trigger: "frame", filters: filterData });
    }
  };

  const handleReset = () => {
    setSelectedFilters({
      make: "",
      model: "",
      engine: "",
      year: "",
      frame: "",
    });
    setModel([]);
    setEngine([]);
    setYear([]);
    setFrame([]);
    setCatalogueId(null);
    setLoading(false);
  };

  return (
    <div className="flex flex-col gap-5 items-center justify-start">
      {loading && (
        <div className="flex absolute top-0 left-0 w-full h-full items-center justify-center z-10 bg-black/20">
          <Spinner color="primary" size="lg" />
        </div>
      )}
      <div
        className={`grid grid-cols-1 w-full gap-3 md:gap-5 ${isSingleColumn ? "" : "md:grid-cols-2"}`}
      >
        <FilterSelect
          label="Make"
          options={make}
          selectedKeys={[selectedFilters.make]}
          onSelectionChange={(keys) =>
            handleFilterChange("make", keys.currentKey || "")
          }
        />
        <FilterSelect
          isDisabled={!selectedFilters.make}
          label="Model"
          options={model}
          selectedKeys={[selectedFilters.model]}
          onSelectionChange={(keys) =>
            handleFilterChange("model", keys.currentKey || "")
          }
        />
        <FilterSelect
          isDisabled={!selectedFilters.model}
          label="Engine CC"
          options={engine}
          selectedKeys={[selectedFilters.engine]}
          onSelectionChange={(keys) =>
            handleFilterChange("engine", keys.currentKey || "")
          }
        />
        <FilterSelect
          isDisabled={!selectedFilters.engine}
          label="Year"
          options={year}
          selectedKeys={[selectedFilters.year]}
          onSelectionChange={(keys) =>
            handleFilterChange("year", keys.currentKey || "")
          }
        />
        <FilterSelect
          isDisabled={!selectedFilters.year}
          label="Model Trim"
          options={frame}
          selectedKeys={[selectedFilters.frame]}
          onSelectionChange={(keys) =>
            handleFilterChange("frame", keys.currentKey || "")
          }
        />
      </div>
      <div className="flex items-center justify-center w-full gap-3 md:gap-5">
        <Button
          className="font-medium md:font-normal text-base md:text-lg bg-[#F5F5F5] border-1 border-[#939393] hover:bg-[#FAFAFA] p-2 md:p-5 w-[120px] md:w-[160px] lg:w-[200px]"
          variant="bordered"
          onPress={handleReset}
        >
          Reset
        </Button>
        <Button
          className="font-medium md:font-normal text-base md:text-lg bg-ax-primary hover:opacity-80 border-1 border-ax-primary p-2 md:p-5 w-[120px] md:w-[160px] lg:w-[200px]"
          isDisabled={!catalogueId}
          variant="flat"
          onPress={() =>
            router.push(
              `${redirectUrl}?catalogue_id=${catalogueId}&make=${selectedFilters.make}&model=${selectedFilters.model}&engine=${selectedFilters.engine}&year=${selectedFilters.year}&frame=${selectedFilters.frame}`,
            )
          }
        >
          Search
        </Button>
      </div>
    </div>
  );
};

export default MakeModelFilter;
